import { useCallback, useRef, useState } from 'react';
import { Upload, FileSpreadsheet, X, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isSupportedFileType } from '@/lib/import';

interface ImportFileUploadProps {
  onFileSelect: (file: File) => void;
  onDownloadTemplate: () => void;
  isLoading?: boolean;
  error?: string | null;
  maxSizeBytes?: number;
}

export function ImportFileUpload({
  onFileSelect,
  onDownloadTemplate,
  isLoading = false,
  error = null,
  maxSizeBytes = 10 * 1024 * 1024, // 10MB default
}: ImportFileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const displayError = error || localError;

  const validateFile = useCallback((file: File): string | null => {
    // Check file type
    if (!isSupportedFileType(file.name)) {
      return 'Unsupported file type. Please use CSV or Excel (.xlsx, .xls) files.';
    }

    // Check file size
    if (file.size > maxSizeBytes) {
      const maxSizeMB = Math.round(maxSizeBytes / 1024 / 1024);
      return `File too large. Maximum size is ${maxSizeMB}MB.`;
    }

    return null;
  }, [maxSizeBytes]);

  const handleFile = useCallback((file: File) => {
    setLocalError(null);
    const validationError = validateFile(file);
    if (validationError) {
      setLocalError(validationError);
      return;
    }
    onFileSelect(file);
  }, [validateFile, onFileSelect]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragging to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFile(files[0]);
    }
  }, [handleFile]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    // Reset input so same file can be selected again
    e.target.value = '';
  }, [handleFile]);

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return (
    <div className="space-y-4">
      {/* Download Template Section */}
      <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
        <div className="space-y-1">
          <p className="text-sm font-medium">Need a template?</p>
          <p className="text-xs text-muted-foreground">
            Download our Excel template with instructions and examples
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={onDownloadTemplate}>
          <FileSpreadsheet className="h-4 w-4 mr-2" />
          Download Template
        </Button>
      </div>

      {/* Drop Zone */}
      <div
        className={cn(
          'relative border-2 border-dashed rounded-lg p-8 transition-colors',
          isDragging
            ? 'border-primary bg-primary/5'
            : 'border-muted-foreground/25 hover:border-muted-foreground/50',
          isLoading && 'pointer-events-none opacity-50'
        )}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="hidden"
          onChange={handleInputChange}
          disabled={isLoading}
        />

        <div className="flex flex-col items-center justify-center space-y-4">
          <div className={cn(
            'p-4 rounded-full',
            isDragging ? 'bg-primary/10' : 'bg-muted'
          )}>
            <Upload className={cn(
              'h-8 w-8',
              isDragging ? 'text-primary' : 'text-muted-foreground'
            )} />
          </div>

          <div className="text-center space-y-2">
            <p className="text-sm font-medium">
              {isDragging ? 'Drop your file here' : 'Drag and drop your file here'}
            </p>
            <p className="text-xs text-muted-foreground">
              or
            </p>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleBrowseClick}
              disabled={isLoading}
            >
              Browse Files
            </Button>
          </div>

          <p className="text-xs text-muted-foreground">
            Supports CSV, Excel (.xlsx, .xls) - Max 10MB
          </p>
        </div>
      </div>

      {/* Error Display */}
      {displayError && (
        <div className="flex items-start gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
          <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-destructive">{displayError}</p>
          </div>
          <button
            onClick={() => setLocalError(null)}
            className="text-destructive hover:text-destructive/80"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}
